import type { CSSProperties, ReactNode } from 'react'
import type { useShellFlyoutPresence } from '@/hooks/use-shell-flyout'
import { cn } from '@/lib/utils'

export type ShellFlyoutPresence = ReturnType<typeof useShellFlyoutPresence>

/**
 * The auto-hide panel that flies over the canvas from the rail.
 *
 * Wire it with the two hooks in `@/hooks/use-shell-flyout`:
 * - `useShellFlyoutHold(close)`: rail/flyout `onMouseEnter` -> `hold.enter()`,
 *   `onMouseLeave` -> `hold.leave()` (500ms hold), canvas click -> `hold.closeNow()`,
 *   and every `...` menu inside -> `onOpenChange={hold.menuOpenChange}` so a
 *   portaled menu does not start the timer.
 * - `useShellFlyoutPresence(show, exitClass)`: pass the result as `presence`.
 *
 * Gallery: /?test=maater-ui#panel-collapse
 */
export function ShellFlyout({
  presence,
  exitClass,
  left,
  width,
  className,
  style,
  testId,
  children,
}: {
  presence: ShellFlyoutPresence
  /** `shellPanelExitClass(...)`. Empty string means instant. */
  exitClass: string
  /** Rail width in px. */
  left: number
  width: number
  className?: string
  style?: CSSProperties
  testId?: string
  children: ReactNode
}) {
  if (!presence.mounted) return null
  return (
    <div
      data-testid={testId}
      className={cn(
        'absolute bottom-0 top-0 z-20 flex border-r border-border bg-sidebar shadow-[1px_0_0_rgba(255,255,255,0.12),8px_0_32px_rgba(0,0,0,0.55)]',
        presence.closing && exitClass,
        presence.closing && 'pointer-events-none',
        className,
      )}
      style={{ left, width, ...style }}
      onAnimationEnd={presence.onAnimationEnd}
    >
      {children}
    </div>
  )
}
